import React from 'react';
import styled from 'styled-components';
import { DexType, V4Pool } from '../types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const Label = styled.label`
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.875rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  min-width: 200px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const DiscoverButton = styled.button`
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.35);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PoolChip = styled.div<{ hasHook?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  background: var(--bg-secondary);
  border: 1px solid ${p => p.hasHook ? '#f59e0b' : 'var(--border-color)'};
  border-left: 3px solid ${p => p.hasHook ? '#f59e0b' : 'var(--accent-primary)'};
  border-radius: 6px;
  font-size: 0.8125rem;
`;

const PoolChipHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const PoolMeta = styled.div`
  color: var(--text-primary);
  font-family: 'SF Mono', Monaco, monospace;
`;

const ClearBtn = styled.button`
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  width: 24px;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  &:hover { background: var(--bg-tertiary); }
`;

const Warning = styled.div`
  color: #f59e0b;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Hint = styled.div`
  color: var(--text-muted);
  font-size: 0.75rem;
  margin-top: 4px;
`;

interface DexSelectorProps {
  dexType: DexType;
  selectedPool?: V4Pool;
  onDexTypeChange: (dexType: DexType) => void;
  onOpenPoolSelector: () => void;
  onClearPool: () => void;
  canDiscover: boolean;       // tokenAddress + ethAddress непустые
}

const truncate = (s: string, head = 8, tail = 4): string =>
  s.length > head + tail + 2 ? `${s.slice(0, head)}…${s.slice(-tail)}` : s;

const formatFee = (pool: V4Pool): string => {
  if (pool.is_dynamic_fee) return 'DYNAMIC';
  return `${(pool.fee_pips / 10000).toFixed(2)}%`;
};

const formatTvl = (v: number | null | undefined): string => {
  if (v == null) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};

const DexSelector: React.FC<DexSelectorProps> = ({
  dexType,
  selectedPool,
  onDexTypeChange,
  onOpenPoolSelector,
  onClearPool,
  canDiscover,
}) => {
  const isV4 = dexType === 'uniswap-v4';
  const isV3 = dexType === 'uniswap-v3';

  return (
    <Container>
      <Row>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Label>🔀 DEX / Routing</Label>
          <Select
            value={dexType}
            onChange={(e) => onDexTypeChange(e.target.value as DexType)}
          >
            <option value="kyberswap">KyberSwap (aggregator)</option>
            <option value="uniswap-v3">Uniswap V3 (direct)</option>
            <option value="uniswap-v4">Uniswap V4 (direct)</option>
          </Select>
        </div>

        {(isV4 || isV3) && (
          <DiscoverButton
            type="button"
            onClick={onOpenPoolSelector}
            disabled={!canDiscover}
            title={!canDiscover ? 'Сначала укажите token и quote token' : 'Найти пулы'}
          >
            🔍 Discover Pools
          </DiscoverButton>
        )}
      </Row>

      {selectedPool && (
        <PoolChip hasHook={selectedPool.has_hook || selectedPool.is_dynamic_fee}>
          <PoolChipHeader>
            <PoolMeta>
              🏊 <strong>{truncate(selectedPool.pool_id)}</strong>
              {' • TVL '}
              {formatTvl(selectedPool.liquidity_usd)}
              {' • fee '}
              {formatFee(selectedPool)}
              {selectedPool.tick_spacing != null && ` • tick ${selectedPool.tick_spacing}`}
            </PoolMeta>
            <ClearBtn type="button" onClick={onClearPool} title="Clear pool">✕</ClearBtn>
          </PoolChipHeader>
          {selectedPool.is_dynamic_fee && (
            <Warning>⚠ Dynamic-fee pool — actual fee is determined by hook on each swap</Warning>
          )}
          {selectedPool.has_hook && !selectedPool.is_dynamic_fee && (
            <Warning>⚠ Has hook {truncate(selectedPool.hooks || '', 6, 4)} — actual fee may differ from base</Warning>
          )}
        </PoolChip>
      )}

      {(isV4 || isV3) && !selectedPool && (
        <Hint>
          {isV4
            ? 'No pool selected — backend will auto-pick the highest-TVL clean pool.'
            : 'V3 requires explicit pool — click "Discover Pools" to choose.'}
        </Hint>
      )}
    </Container>
  );
};

export default DexSelector;
